import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    ClipboardDocumentListIcon,
    CheckCircleIcon as SolidCheckCircleIcon,
    UsersIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    EyeSlashIcon,
    KeyIcon
} from '@heroicons/react/24/solid';
import { XCircleIcon, CheckCircleIcon as OutlineCheckCircleIcon } from '@heroicons/react/24/outline';
import './Login.css';

// --- API Endpoints and Keys ---
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const LOGIN_API_ENDPOINT = `${API_BASE_URL}/login`;
const VERIFY_OTP_API_ENDPOINT = `${API_BASE_URL}/public/verify-otp`;
const REGISTER_API_ENDPOINT = `${API_BASE_URL}/register`;
const AUTH_TOKEN_KEY = 'authToken';
const USER_DETAILS_KEY = 'loggedInUserDetails';

const MessageDisplay = React.memo(({ message, type }) => {
  if (!message) return null;
  const Icon = type === 'success' ? SolidCheckCircleIcon : ExclamationTriangleIcon;
  const colors = type === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700';
  const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';
  return (
    <div className={`message-box ${colors}`} role="alert">
      <div className="message-box-icon-wrapper"><Icon className={`message-box-icon ${iconColor}`} /></div>
      <div className="message-box-content"><p className="message-box-text">{message}</p></div>
    </div>
  );
});

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    location.pathname.toLowerCase().includes('/signup') ? 'register' : 'login'
  );

  // States for Login
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // States for OTP
  const [loginStep, setLoginStep] = useState('credentials');
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [userEmailForOtp, setUserEmailForOtp] = useState('');
  const [timer, setTimer] = useState(60); // 1 minute in seconds
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const otpInputRefs = useRef([]);
  const verifyButtonRef = useRef(null);
  const timerRef = useRef(null); // Ref to manage timer interval

  // States for Registration
  const [registerFullName, setRegisterFullName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);

  // State for Password Complexity
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  // State for Notifications
  const [notification, setNotification] = useState({ message: '', type: '' });

  // Effect to handle notifications passed via router state
  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Effect to redirect if already logged in
  useEffect(() => {
    if (localStorage.getItem(AUTH_TOKEN_KEY)) navigate('/dashboard');
  }, [navigate]);

  // Effect to sync active tab with URL
  useEffect(() => {
    const newTab = location.pathname.toLowerCase().includes('/signup') ? 'register' : 'login';
    if (newTab !== activeTab) {
      setActiveTab(newTab);
      if (!location.state?.notification) {
        setNotification({ message: '', type: '' });
      }
    }
  }, [location.pathname, activeTab, location.state]);

  // Effect for real-time password validation
  useEffect(() => {
    if (activeTab === 'register' && registerPassword) {
      const checks = {
        length: registerPassword.length >= 8,
        uppercase: /[A-Z]/.test(registerPassword),
        lowercase: /[a-z]/.test(registerPassword),
        number: /[0-9]/.test(registerPassword),
        specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/]+/.test(registerPassword),
      };
      setPasswordCriteria(checks);
    }
  }, [registerPassword, activeTab]);

  // Effect to handle OTP countdown timer using ref
  useEffect(() => {
    if (loginStep === 'otp' && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(timerRef.current); // Cleanup on unmount
  }, [loginStep, timer]);

  // Effect to clear notification after 5 seconds
  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000); // Clear after 5 seconds
      return () => clearTimeout(timer); // Cleanup timer on unmount or re-render
    }
  }, [notification.message]);

  // Format timer to mm:ss
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Clears notifications as user types in any input
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    if (notification.message) {
      setNotification({ message: '', type: '' });
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginIdentifier || !loginPassword) {
      setNotification({ message: "Please enter both username/email and password.", type: 'error' });
      return;
    }
    setIsLoginLoading(true);
    setNotification({ message: '', type: '' });

    try {
      const response = await fetch(LOGIN_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({ username: loginIdentifier, password: loginPassword }),
      });

      const responseText = await response.text();
      let data = {};
      try { data = JSON.parse(responseText); }
      catch (err) {
        data = { message: responseText };
      }

      if (response.ok) {
        const message = data?.message || '';
        const isOtpFlow = message.toLowerCase().includes('otp sent successfully');

        if (isOtpFlow) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginIdentifier)) {
            setNotification({ message: "An email is required for Two-Factor Authentication.", type: 'error' });
            setIsLoginLoading(false);
            return;
          }
          setUserEmailForOtp(loginIdentifier);
          setLoginStep('otp');
          setTimer(60); // Reset timer to 1 minute
          setIsResendDisabled(true); // Disable resend button
          setOtp(new Array(6).fill("")); // Clear OTP inputs
          setNotification({
            message: message,
            type: 'success'
          });
          setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
        } else {
          const token = data["token : "] || data["token :"] || data.token;
          const user = data.user;

          if (token && user) {
            setNotification({ message: 'Login successful! Redirecting...', type: 'success' });
            localStorage.setItem(AUTH_TOKEN_KEY, token);
            localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(user));
            window.loggedInUser = user;
            setTimeout(() => navigate('/dashboard'), 1500);
          } else {
            setNotification({ message: "Login response was incomplete. Please contact support.", type: 'error' });
          }
        }
      } else {
        let errorMessage;
        if (response.status === 401) {
          errorMessage = data?.message || "Invalid credentials. Please check your username and password.";
        } else {
          errorMessage = data?.message || `Login failed with status: ${response.status}. Please try again.`;
        }
        setNotification({ message: errorMessage, type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred. Please check your connection and try again.', type: 'error' });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (isResendDisabled || isOtpVerifying) return;

    setIsOtpVerifying(true);
    setNotification({ message: '', type: '' });

    try {
      const response = await fetch(LOGIN_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify({ username: loginIdentifier, password: loginPassword }), // Use current credentials
      });

      const responseText = await response.text();
      let data = {};
      try { data = JSON.parse(responseText); }
      catch (err) {
        data = { message: responseText };
      }

      if (response.ok && data?.message?.toLowerCase().includes('otp sent successfully')) {
        setNotification({ message: 'New OTP sent successfully!', type: 'success' });
        setTimer(60); // Reset timer to 1 minute
        setIsResendDisabled(true); // Disable resend button
        setOtp(new Array(6).fill("")); // Clear OTP inputs
        otpInputRefs.current[0]?.focus();
      } else {
        setNotification({
          message: data?.message || 'Failed to resend OTP. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred while resending OTP.', type: 'error' });
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleOtpVerify = useCallback(async (e) => {
    if (e) e.preventDefault();

    if (isOtpVerifying) return;

    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      if (e) setNotification({ message: "Please enter the complete 6-digit OTP.", type: 'error' });
      return;
    }

    setIsOtpVerifying(true);
    setNotification({ message: '', type: '' });

    try {
      const url = new URL(VERIFY_OTP_API_ENDPOINT);
      url.search = new URLSearchParams({ email: userEmailForOtp, otp: enteredOtp }).toString();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: ''
      });

      const data = await response.json().catch(() => ({}));
      const token = data["token : "] || data["token :"] || data.token;
      const user = data.user;

      if (token && user) {
        setNotification({ message: 'Verification successful! Logging you in...', type: 'success' });
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(user));
        window.loggedInUser = user;
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        let errorMessage = "An unknown error occurred.";
        if (!response.ok) {
          switch (response.status) {
            case 400: errorMessage = data?.message || "Failed to verify OTP. Please try again."; break;
            case 403: errorMessage = data?.message || "Invalid OTP. Please check the code and try again."; break;
            case 500: errorMessage = data?.message || "An internal server error occurred."; break;
            default: errorMessage = data?.message || `OTP verification failed with status: ${response.status}`;
          }
        } else {
          errorMessage = "OTP verification failed: Server response is incomplete.";
        }
        setNotification({ message: errorMessage, type: 'error' });
        setOtp(new Array(6).fill(""));
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setNotification({ message: 'A network error occurred during OTP verification.', type: 'error' });
    } finally {
      setIsOtpVerifying(false);
    }
  }, [otp, userEmailForOtp, navigate, isOtpVerifying]);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!registerFullName || !registerEmail || !registerPassword || !registerConfirmPassword) {
      setNotification({ message: "Please fill in all fields to register.", type: 'error' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerEmail)) {
      setNotification({ message: "Please enter a valid email address.", type: 'error' });
      return;
    }

    const allCriteriaMet = Object.values(passwordCriteria).every(Boolean);
    if (!allCriteriaMet) {
      setNotification({ message: "Password does not meet all complexity requirements.", type: 'error' });
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setNotification({ message: "Passwords do not match. Please try again.", type: 'error' });
      return;
    }

    setIsRegisterLoading(true);
    const payload = { name: registerFullName, email: registerEmail, password: registerPassword, active: true };
    try {
      const response = await fetch(REGISTER_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let data = {};
      try { data = JSON.parse(responseText); }
      catch (err) { console.warn("API response was not valid JSON:", responseText); }

      if (response.ok) {
        navigate('/login', {
          replace: true,
          state: {
            notification: {
              message: 'Registration successful! Please log in to continue.',
              type: 'success'
            }
          }
        });
      } else {
        let errorMessage;
        switch (response.status) {
          case 400:
            errorMessage = data?.message || "This email is already registered. Please try another or log in.";
            break;
          default:
            errorMessage = data?.message || `Registration failed. Please try again later.`;
        }
        setNotification({ message: errorMessage, type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred during registration.', type: 'error' });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    const value = element.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Clear notification when user starts entering OTP
    if (notification.message && !otp.some(digit => digit)) {
      setNotification({ message: '', type: '' });
    }

    if (notification.message) {
      setNotification({ message: '', type: '' });
    }

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      digits.forEach((digit, index) => {
        if (otpInputRefs.current[index]) {
          otpInputRefs.current[index].value = digit;
        }
      });
      setTimeout(() => verifyButtonRef.current?.focus(), 0);
      // Clear notification when OTP is pasted
      if (notification.message) {
        setNotification({ message: '', type: '' });
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const switchTab = (newTab) => {
    setActiveTab(newTab);
    setLoginStep('credentials');
    setNotification({ message: '', type: '' });
    navigate(newTab === 'register' ? '/signup' : '/login', { replace: true });
  };

  const PasswordStrengthIndicator = ({ criteria, password }) => {
    if (!password) return null;

    const allCriteria = [
      { key: 'length', label: 'At least 8 characters' },
      { key: 'uppercase', label: 'At least one uppercase letter (A-Z)' },
      { key: 'lowercase', label: 'At least one lowercase letter (a-z)' },
      { key: 'number', label: 'At least one number (0-9)' },
      { key: 'specialChar', label: 'At least one special character (!@#$...)' },
    ];

    const unmetCriteria = allCriteria.filter(rule => !criteria[rule.key]);

    if (unmetCriteria.length === 0) {
      return (
        <div className="password-strength-guide">
          <ul className="password-guide-list">
            <li className="criteria-item criteria-met">
              <OutlineCheckCircleIcon className="criteria-icon" />
              <span>Great! Your Password Is Complex.</span>
            </li>
          </ul>
        </div>
      );
    }

    return (
      <div className="password-strength-guide">
        <p className="password-guide-title">Remaining requirements:</p>
        <ul className="password-guide-list">
          {unmetCriteria.map(rule => (
            <li key={rule.key} className="criteria-item criteria-unmet">
              <XCircleIcon className="criteria-icon" />
              <span>{rule.label}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const isForgotPasswordDisabled = !loginIdentifier.trim();

  return (
    <div className="auth-page-container">
      <div className="auth-left-section">
        <div className="auth-left-content">
          <div className="logo-container">
            <div className="logo-icon-wrapper"><ClipboardDocumentListIcon className="logo-icon" /></div>
            <h1 className="logo-text">ZTool</h1>
          </div>
          <h2 className="left-heading">Your Gateway to ZTool Services</h2>
          <p className="left-description">Securely log in or create an account to access our integrated suite of ZTool applications and platforms. One account, many possibilities.</p>
          <div className="feature-boxes-container">
            <div className="feature-box">
              <div className="feature-box-header"><SolidCheckCircleIcon className="feature-icon" /><h3 className="feature-title">Unified Access</h3></div>
              <p className="feature-description">Access all your ZTool services with a single set of credentials. Streamlined, simple, and secure.</p>
            </div>
            <div className="feature-box">
              <div className="feature-box-header"><UsersIcon className="feature-icon" /><h3 className="feature-title">Centralized Hub</h3></div>
              <p className="feature-description">This is your central point for managing your account and accessing various tools designed to empower your workflow.</p>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-right-section">
        <div className="auth-form-container">
          <div className="mobile-logo-container">
            <div className="mobile-logo-icon-wrapper"><ClipboardDocumentListIcon className="mobile-logo-icon" /></div>
            <h1 className="mobile-logo-text">ZTool</h1>
          </div>
          <MessageDisplay message={notification.message} type={notification.type} />
          {activeTab === 'login' && loginStep === 'otp' ? (
            <div className="otp-container">
              <div className="card-header">
                <KeyIcon className="otp-heading-icon"/>
                <h2 className="form-heading">Check Your Email</h2>
                <p className="form-subheading">We've sent a 6-digit code to <strong className="user-email-emphasis">{userEmailForOtp}</strong>. Please enter it below.</p>
                <p className="otp-timer">Time remaining: {formatTimer(timer)}</p>
              </div>
              <form onSubmit={handleOtpVerify} className="form-content" noValidate>
                <div className="otp-input-group">
                  {otp.map((data, index) => (
                    <input
                      key={index}
                      type="text"
                      className="otp-input"
                      maxLength="1"
                      value={data}
                      onChange={(e) => handleOtpChange(e.target, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      onFocus={(e) => e.target.select()}
                      onPaste={handleOtpPaste}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      disabled={isOtpVerifying}
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>
                <button type="submit" className="submit-button" disabled={isOtpVerifying} ref={verifyButtonRef}>
                  {isOtpVerifying ? 'Verifying...' : 'Verify & Sign In'}
                </button>
                <button
                  type="button"
                  className="resend-otp-button"
                  onClick={handleResendOtp}
                  disabled={isResendDisabled || isOtpVerifying}
                >
                  Resend OTP
                </button>
              </form>
              <div className="tab-switch-prompt">
                <button
                  className="tab-switch-link"
                  onClick={() => { setLoginStep('credentials'); setNotification({ message: '', type: '' }); }}
                  disabled={isOtpVerifying}
                >
                  ← Go Back to Login
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="card-header">
                <h2 className="form-heading">{activeTab === 'login' ? 'Sign In to ZTool' : 'Create an Account'}</h2>
                <p className="form-subheading">{activeTab === 'login' ? 'Enter your credentials to continue' : 'Enter your information to get started'}</p>
              </div>
              <div className="tabs-container">
                <div className="tabs-list">
                  <button className={`tab-trigger ${activeTab === 'login' ? 'active-tab' : ''}`} onClick={() => switchTab('login')} disabled={isLoginLoading || isRegisterLoading}>Login</button>
                  <button className={`tab-trigger ${activeTab === 'register' ? 'active-tab' : ''}`} onClick={() => switchTab('register')} disabled={isLoginLoading || isRegisterLoading}>Register</button>
                </div>
              </div>
              {activeTab === 'login' && (
                <form onSubmit={handleLoginSubmit} className="form-content" noValidate>
                  <div className="form-item">
                    <label htmlFor="login-identifier" className="input-label">Username or Email</label>
                    <input id="login-identifier" type="text" value={loginIdentifier} onChange={handleInputChange(setLoginIdentifier)} placeholder="e.g., your.email@example.com" className="form-input" autoComplete="username" disabled={isLoginLoading}/>
                  </div>
                  <div className="form-item password-wrapper">
                    <div className="label-row">
                      <label htmlFor="login-password" className="input-label">Password</label>
                      <Link
                        to="/reset-password"
                        state={{ email: loginIdentifier }}
                        className={`forgot-password-link ${isForgotPasswordDisabled ? 'disabled' : ''}`}
                        onClick={(e) => { if(isForgotPasswordDisabled) e.preventDefault(); }}
                        title={isForgotPasswordDisabled ? "Please enter an email to reset password" : "Reset your password"}
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <div className="password-input-container">
                      <input id="login-password" type={showLoginPassword ? 'text' : 'password'} value={loginPassword} onChange={handleInputChange(setLoginPassword)} placeholder="Your secure password" className="form-input" autoComplete="current-password" disabled={isLoginLoading}/>
                      <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="password-toggle-btn" disabled={isLoginLoading}>
                        {showLoginPassword ? <EyeSlashIcon className="password-toggle-icon" /> : <EyeIcon className="password-toggle-icon" />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className="submit-button" disabled={isLoginLoading}>
                    {isLoginLoading ? 'Processing...' : 'Continue'}
                  </button>
                </form>
              )}
              {activeTab === 'register' && (
                <form onSubmit={handleRegisterSubmit} className="form-content" noValidate>
                  <div className="form-item">
                    <label htmlFor="register-fullname" className="input-label">Full Name</label>
                    <input id="register-fullname" type="text" value={registerFullName} onChange={handleInputChange(setRegisterFullName)} placeholder="Enter your full name" className="form-input" autoComplete="name" disabled={isRegisterLoading} required/>
                  </div>
                  <div className="form-item">
                    <label htmlFor="register-email" className="input-label">Email</label>
                    <input id="register-email" type="email" value={registerEmail} onChange={handleInputChange(setRegisterEmail)} placeholder="Enter your email" className="form-input" autoComplete="email" disabled={isRegisterLoading} required/>
                  </div>
                  <div className="form-item password-wrapper">
                    <label htmlFor="register-password" className="input-label">Create Password</label>
                    <div className="password-input-container">
                      <input id="register-password" type={showRegisterPassword ? 'text' : 'password'} value={registerPassword} onChange={handleInputChange(setRegisterPassword)} placeholder="Choose a secure password" className="form-input" autoComplete="new-password" disabled={isRegisterLoading} required/>
                      <button type="button" onClick={() => setShowRegisterPassword(!showRegisterPassword)} className="password-toggle-btn" disabled={isRegisterLoading}>
                        {showRegisterPassword ? <EyeSlashIcon className="password-toggle-icon" /> : <EyeIcon className="password-toggle-icon" />}
                      </button>
                    </div>
                    <PasswordStrengthIndicator criteria={passwordCriteria} password={registerPassword} />
                  </div>
                  <div className="form-item password-wrapper">
                    <label htmlFor="register-confirm-password" className="input-label">Confirm Password</label>
                    <div className="password-input-container">
                      <input id="register-confirm-password" type={showRegisterConfirmPassword ? 'text' : 'password'} value={registerConfirmPassword} onChange={handleInputChange(setRegisterConfirmPassword)} placeholder="Confirm your password" className="form-input" autoComplete="new-password" disabled={isRegisterLoading} required/>
                      <button type="button" onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)} className="password-toggle-btn" disabled={isRegisterLoading}>
                        {showRegisterConfirmPassword ? <EyeSlashIcon className="password-toggle-icon" /> : <EyeIcon className="password-toggle-icon" />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className="submit-button" disabled={isRegisterLoading}>
                    {isRegisterLoading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>
              )}
              <div className="tab-switch-prompt">
                <p>{activeTab === 'login' ? "Don't have an account? " : 'Already have an account? '}
                  <button className="tab-switch-link" onClick={() => switchTab(activeTab === 'login' ? 'register' : 'login')} disabled={isLoginLoading || isRegisterLoading}>
                    {activeTab === 'login' ? 'Sign up' : 'Login'}
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login; 